import { getInitialOnboardingStep } from '~/helpers/onboarding'
import { getCompletedAwards, type AwardType } from '~/pages/awards/helper'
import { useAuthStore } from '~/stores/token'
import { useUserStore } from '~/stores/user'
import { IUserType } from '~/types/UserData'
// Subpath, not the barrel: the barrel pulls in zod (#1982).
import { isEntraUser } from 'microsoft-auth/provider'

export default defineNuxtRouteMiddleware(async to => {
  const { status } = useAuth()

  const authStore = useAuthStore()
  const userStore = useUserStore()

  // Allow demo pages
  if (to.path.startsWith('/billecta-id')) {
    return
  }

  // Allow for preview mode
  const preview_mode = useRuntimeConfig().public.CTF_PREVIEW_MODE
  if (preview_mode === 'true' && to.path.startsWith('/preview/') && to.params.id) {
    return
  }
  if (preview_mode === 'true' && to.path.startsWith('/awards/') && to.params.name) {
    return
  }
  // Set token and user if authenticated (skip if already loaded)
  if (status.value === 'authenticated') {
    await authStore.setToken()

    const userAlreadyLoaded = userStore.getUser?.id && userStore.getUser.id !== 'init'

    if (!userAlreadyLoaded) {
      if (isEntraUser(authStore.token)) {
        // Entra users: populate store from JWT + Firebase, skip Auth0
        await userStore.setEntraUser({
          sub: authStore.token.sub,
          name: authStore.token.name,
          email: authStore.token.email,
        })
      } else {
        await userStore.setUser(authStore.getTokenId as string)
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('authStore.getTokenId', authStore.getTokenId)
    }
  }

  // Redirect to login if not authenticated
  if (status.value !== 'authenticated' && to.path !== '/login') {
    return navigateTo('/login')
  }

  // Redirect to init if authenticated and no preferences
  if (
    status.value === 'authenticated' &&
    !isEntraUser(authStore.token) &&
    (!userStore.getUser || Object.keys(userStore.getUserPreferences).length === 0) &&
    to.path !== '/init'
  ) {
    return navigateTo('/init')
  }

  // Redirect to onboarding if required
  // TODO: uncomment this
  /* if (status.value === 'authenticated' && to.path !== '/onboarding') {
    const userOnboardingData = userStore.getUser?.onboarding
    const nextStep = getInitialOnboardingStep(userOnboardingData)
    if (nextStep) {
      return navigateTo('/onboarding')
    }
  }

  if (status.value === 'authenticated' && to.path === '/onboarding') {
    const userOnboardingData = userStore.getUser?.onboarding
    const nextStep = getInitialOnboardingStep(userOnboardingData)
    if (!nextStep) {
      return navigateTo('/')
    }
  } */

  // TODO: remove this
  if (status.value === 'authenticated' && to.path === '/onboarding') {
    return navigateTo('/')
  }

  if (
    status.value === 'authenticated' &&
    ['send-mails', 'stats', 'send-particular', 'users'].includes(to.path) &&
    !isEntraUser(authStore.token) &&
    (userStore.getUserRole?.id !== IUserType.ADMIN ||
      !['admin'].includes((userStore.getUser?.userAccess as string) || 'user'))
  ) {
    return navigateTo('/')
  }

  // Redirect to home if authenticated and has preferences
  if (
    (status.value === 'authenticated' && to.path === '/login') ||
    (to.path === '/init' && Object.keys(userStore.getUserPreferences).length > 0)
  ) {
    return navigateTo('/')
  }

  // Allow access to awards/[name] only if the award is completed
  if (status.value === 'authenticated' && to.path.startsWith('/awards/') && to.params.name) {
    const actions = userStore.getUserActions

    const completedAwards = getCompletedAwards(actions).map(award => award.type) // Get completed award types

    const awardName = to.params.name as AwardType

    if (!completedAwards.includes(awardName)) {
      return navigateTo('/awards') // Redirect to awards list if award is not completed
    }
  }
})
